import {
  loginApi,
  logoutApi,
  googleLoginApi,
  zaloLoginApi
} from "@/modules/authentication/api/api-auth";
import { getUserMe, updateUserProfile, UpdateUserProfilePayload } from "@/modules/user/api/user.api";
import { userAction } from "@/modules/user/reducer/user.slice";
import { User } from "@/modules/user/types/user.type";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";


interface JwtPayload {
  sub?: string;
  scope?: string;
}

const decodeJwtPayload = (token: string): JwtPayload | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = decodeURIComponent(
      window
        .atob(normalized)
        .split("")
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
    return JSON.parse(decoded) as JwtPayload;
  } catch {
    return null;
  }
};

const normalizePermissionCode = (value: unknown): string =>
  String(value ?? "").trim().toUpperCase();

const createFallbackUser = (token: string): User => {
  const payload = decodeJwtPayload(token);
  const username = payload?.sub || "Người dùng";
  const scopeCodes = (payload?.scope?.split(/\s+/) ?? [])
    .map(normalizePermissionCode)
    .filter(Boolean);

  // Khi /users/myInfo tạm lỗi, vẫn giữ đúng quyền có trong JWT scope.
  const permissions = scopeCodes
    .filter(code => !code.startsWith("ROLE_"))
    .map(code => ({
      id: code,
      code,
      name: code,
      description: "",
      createdAt: "",
      updatedAt: "",
    }));

  const isSystemAdmin = scopeCodes.includes("SYSTEM_ADMIN") ||
    scopeCodes.includes("ROLE_SYSTEM_ADMIN") ||
    scopeCodes.includes("ROLE_ADMIN");

  return {
    id: username,
    username,
    fullName: username,
    email: "",
    userPermission: [],
    permissions,
    role: {
      id: "jwt-fallback-role",
      name: "JWT fallback",
      permissions,
    },
    devices: [],
    rooms: [],
    type: isSystemAdmin ? "admin" : "user",
    status: "active",
    createdAt: "",
    updatedAt: "",
  };
};

const normalizeUser = (raw: any): User => {
  const rolePermissions = Array.isArray(raw?.role?.permissions)
    ? raw.role.permissions.map((permission: any) => ({
        ...permission,
        code: normalizePermissionCode(permission?.code),
      }))
    : [];
  const directPermissions = Array.isArray(raw?.permissions)
    ? raw.permissions.map((permission: any) => ({
        ...permission,
        code: normalizePermissionCode(permission?.code),
      }))
    : rolePermissions;
  const isSystemAdmin = [...rolePermissions, ...directPermissions]
    .some((permission: any) => permission?.code === "SYSTEM_ADMIN");

  return {
    id: String(raw?.id ?? raw?.username ?? ''),
    username: String(raw?.username ?? ''),
    fullName: String(raw?.fullName ?? raw?.username ?? 'Người dùng'),
    email: String(raw?.email ?? ''),
    phone: raw?.phone ? String(raw.phone) : undefined,
    userPermission: Array.isArray(raw?.userPermission) ? raw.userPermission : [],
    permissions: directPermissions,
    role: raw?.role ? { ...raw.role, permissions: rolePermissions } : undefined,
    organizationId: raw?.organizationId ? String(raw.organizationId) : undefined,
    organizationName: raw?.organizationName ? String(raw.organizationName) : undefined,
    sipCode: raw?.sipCode ? String(raw.sipCode) : undefined,
    sipUsername: raw?.sipUsername ? String(raw.sipUsername) : raw?.sipCode ? String(raw.sipCode) : undefined,
    sipPassword: raw?.sipPassword ? String(raw.sipPassword) : raw?.sipCredential?.password ? String(raw.sipCredential.password) : undefined,
    sipDomain: raw?.sipDomain ? String(raw.sipDomain) : raw?.sipCredential?.domain ? String(raw.sipCredential.domain) : undefined,
    sipWsUrl: raw?.sipWsUrl ? String(raw.sipWsUrl) : raw?.sipCredential?.wsUrl ? String(raw.sipCredential.wsUrl) : undefined,
    devices: Array.isArray(raw?.devices) ? raw.devices : [],
    deviceGroups: Array.isArray(raw?.deviceGroups) ? raw.deviceGroups : [],
    rooms: Array.isArray(raw?.rooms) ? raw.rooms : [],
    type: isSystemAdmin ? 'admin' : 'user',
    status: String(raw?.status ?? 'ACTIVE').toLowerCase(),
    isOnline: Boolean(raw?.isOnline),
    lastActiveAt: raw?.lastActiveAt,
    createdAt: String(raw?.createdAt ?? ''),
    updatedAt: String(raw?.updatedAt ?? ''),
  };
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    username: string,
    password: string,
    rememberMe: boolean
  ) => Promise<{ success: boolean; message: string }>;
  loginWithGoogle: (
    idToken: string,
    rememberMe: boolean
  ) => Promise<{ success: boolean; message: string }>;
  loginWithZalo: (
    oauth_code: string,
    rememberMe: boolean
  ) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  updateUserProfile: (data: UpdateUserProfilePayload) => Promise<{ success: boolean; message: string }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  // Check if user is already logged in on app start
  const checkAuthState = useCallback(async () => {
    const token = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
    setIsLoading(true);

    if (token) {
      try {
        // Call getUserMe API to verify token
        const userMe = await getUserMe();
        if (userMe.data) {
          const normalizedUser = normalizeUser(userMe.data);
          setUser(normalizedUser);
          dispatch(userAction.setUserMe(normalizedUser));
        }
      } catch (error: unknown) {
        // BE /users/myInfo đang trả code 9999. Không đăng xuất người dùng
        // khi access token vẫn hợp lệ; dùng thông tin tối thiểu từ JWT.
        const fallbackUser = createFallbackUser(token);
        setUser(fallbackUser);
        dispatch(userAction.setUserMe(fallbackUser));
        localStorage.setItem("user_data", JSON.stringify(fallbackUser));
        console.error("Cannot load /users/myInfo, using JWT fallback:", error);
      }
    } else {
      // No token, clear any existing user data
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
      localStorage.removeItem("user_data");
      setUser(null);
    }

    setIsLoading(false);
  }, [dispatch]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const userMe = await getUserMe();
      if (userMe.data) {
        const normalizedUser = normalizeUser(userMe.data);
        setUser(normalizedUser);
        dispatch(userAction.setUserMe(normalizedUser));
      }
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  }, [dispatch]);

  // Update user profile
  const handleUpdateUserProfile = useCallback(
    async (data: UpdateUserProfilePayload): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await updateUserProfile(data);
        if (response.success) {
          // Refresh user data after successful update
          await refreshUser();
          return { success: true, message: "Cập nhật thông tin thành công!" };
        } else {
          return { success: false, message: response.message || "Cập nhật thất bại!" };
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Cập nhật thông tin thất bại!";
        return { success: false, message: errorMessage };
      }
    },
    [refreshUser]
  );

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  const login = useCallback(
    async (
      username: string,
      password: string,
      rememberMe: boolean
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await loginApi(username, password);
        if (response.success) {
          if (rememberMe) {
            localStorage.setItem("auth_token", response.data.accessToken);
          } else {
            sessionStorage.setItem("auth_token", response.data.accessToken);
          }
          // Đánh dấu đăng nhập ngay bằng dữ liệu tối thiểu trong JWT.
          // Sau đó thử lấy hồ sơ đầy đủ từ /users/myInfo.
          const fallbackUser = createFallbackUser(response.data.accessToken);
          setUser(fallbackUser);
          dispatch(userAction.setUserMe(fallbackUser));
          localStorage.setItem("user_data", JSON.stringify(fallbackUser));

          try {
            const userMe = await getUserMe();
            if (userMe.data) {
              const normalizedUser = normalizeUser(userMe.data);
              setUser(normalizedUser);
              dispatch(userAction.setUserMe(normalizedUser));
              localStorage.setItem("user_data", JSON.stringify(normalizedUser));
            }
          } catch (error) {
            console.error("Cannot load /users/myInfo, keeping JWT fallback:", error);
          }
          return { success: true, message: response.message };
        } else {
          return { success: false, message: response.message };
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Đăng nhập thất bại!";
        return { success: false, message: errorMessage };
      }
    },
    [dispatch]
  );

  const loginWithGoogle = useCallback(
    async (
      idToken: string,
      rememberMe: boolean
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await googleLoginApi(idToken);
        if (response.success) {
          if (rememberMe) {
            localStorage.setItem("auth_token", response.data.accessToken);
          } else {
            sessionStorage.setItem("auth_token", response.data.accessToken);
          }
          try {
            const userMe = await getUserMe();
            if (userMe.data) {
              const normalizedUser = normalizeUser(userMe.data);
              setUser(normalizedUser);
              dispatch(userAction.setUserMe(normalizedUser));
              localStorage.setItem("user_data", JSON.stringify(normalizedUser));
            }
          } catch (error) {
            console.error("Error fetching user data after Google login:", error);
          }
          return { success: true, message: response.message };
        }
        return { success: false, message: response.message };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Đăng nhập Google thất bại!";
        return { success: false, message: errorMessage };
      }
    },
    [dispatch]
  );

  const loginWithZalo = useCallback(
    async (
      oauth_code: string,
      rememberMe: boolean
    ): Promise<{ success: boolean; message: string }> => {
      try {
        const response = await zaloLoginApi(oauth_code);
        if (response.success) {
          if (rememberMe) {
            localStorage.setItem("auth_token", response.data.accessToken);
          } else {
            sessionStorage.setItem("auth_token", response.data.accessToken);
          }
          try {
            const userMe = await getUserMe();
            if (userMe.data) {
              const normalizedUser = normalizeUser(userMe.data);
              setUser(normalizedUser);
              dispatch(userAction.setUserMe(normalizedUser));
              localStorage.setItem("user_data", JSON.stringify(normalizedUser));
            }
          } catch (error) {
            console.error("Error fetching user data after Zalo login:", error);
          }
          return { success: true, message: response.message };
        }
        return { success: false, message: response.message };
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : "Đăng nhập Zalo thất bại!";
        return { success: false, message: errorMessage };
      }
    },
    [dispatch]
  );

  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Error during logout:", error);
    }
    setUser(null);
    localStorage.removeItem("auth_token");
    sessionStorage.removeItem("auth_token");
    localStorage.removeItem("user_data");
  }, []);

  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      loginWithGoogle,
      loginWithZalo,
      logout,
      updateUserProfile: handleUpdateUserProfile,
      refreshUser,
    }),
    [user, isLoading, login, loginWithGoogle, loginWithZalo, logout, handleUpdateUserProfile, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
